import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Link,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import { 
  LocationOn as LocationOnIcon,
  LocationOff as LocationOffIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import api from '../config/api';
import useGeolocation from '../hooks/useGeolocation';
import { useGlobalWebSocket } from '../contexts/GlobalWebSocketContext';
import { DOCUMENT, documentAddButtonSx, documentMapBoxSx, getDocumentInitials } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';
import { bindGoogleMapResize } from '../utils/googleMapLayout';

// Google Maps Map Component
const MapComponent = React.memo(({ memberLocations, userLocation, onLoad, isMobile }) => {
  const mapRef = useRef(null);
  const markersRef = useRef(new Map()); // Use Map for O(1) lookups
  const infoWindowsRef = useRef(new Map());
  const onLoadRef = useRef(onLoad);
  const mapInstanceRef = useRef(null);
  const isInitializedRef = useRef(false);
  const updateTimeoutRef = useRef(null);
  const resizeUnbindRef = useRef(null);

  // Function to update markers without reinitializing the map
  const updateMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !window.google || !window.google.maps) return;

    // Track which markers we've processed to avoid duplicates
    const processedMembers = new Set();

    // Group members by similar locations (within ~10 meters)
    const locationGroups = [];
    memberLocations.forEach(member => {
      // Skip members without valid coordinates
      if (!member.latitude || !member.longitude || 
          isNaN(Number(member.latitude)) || isNaN(Number(member.longitude))) {
        return;
      }

      const memberPos = {
        lat: Number(member.latitude),
        lng: Number(member.longitude)
      };

      // Find existing group with similar location
      const existingGroup = locationGroups.find(group => {
        const groupPos = group.position;
        const distance = Math.sqrt(
          Math.pow(memberPos.lat - groupPos.lat, 2) + 
          Math.pow(memberPos.lng - groupPos.lng, 2)
        );
        return distance < 0.0001; // ~10 meters
      });

      if (existingGroup) {
        existingGroup.members.push(member);
      } else {
        locationGroups.push({
          position: memberPos,
          members: [member]
        });
      }
    });

    // Update or create markers for each location group
    locationGroups.forEach((group, groupIndex) => {
      group.members.forEach((member, memberIndex) => {
        const memberId = member.id;
        processedMembers.add(memberId);

        // Calculate slight offset for overlapping markers
        const offsetAmount = 0.0001; // Small offset
        const angle = (memberIndex * 2 * Math.PI) / group.members.length;
        const offsetLat = memberIndex > 0 ? Math.cos(angle) * offsetAmount : 0;
        const offsetLng = memberIndex > 0 ? Math.sin(angle) * offsetAmount : 0;

        const position = {
          lat: group.position.lat + offsetLat,
          lng: group.position.lng + offsetLng
        };

        // Check if marker already exists and update position if needed
        const existingMarker = markersRef.current.get(memberId);
        if (existingMarker) {
          // Update existing marker position if it changed significantly
          const currentPos = existingMarker.getPosition();
          if (currentPos) {
            const distance = Math.sqrt(
              Math.pow(currentPos.lat() - position.lat, 2) + 
              Math.pow(currentPos.lng() - position.lng, 2)
            );
            
            if (distance > 0.00001) { // Only update if moved more than ~1 meter
              existingMarker.setPosition(position);
            }
          } else {
            // If no position, set it now
            existingMarker.setPosition(position);
          }
        } else {
          // Create new marker
          const markerSize = isMobile ? 28 : 32;

          const marker = new window.google.maps.Marker({
            position,
            map: mapInstanceRef.current,
            title: member.username,
            icon: {
              url: member.role === 'creator' 
                ? 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="12" fill="#1976d2" stroke="white" stroke-width="3"/>
                      <text x="16" y="20" text-anchor="middle" fill="white" font-size="10" font-family="Arial, sans-serif" font-weight="bold">
                        ${member.username.charAt(0).toUpperCase()}
                      </text>
                    </svg>
                  `)
                : 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="12" fill="#2e7d32" stroke="white" stroke-width="3"/>
                      <text x="16" y="20" text-anchor="middle" fill="white" font-size="10" font-family="Arial, sans-serif" font-weight="bold">
                        ${member.username.charAt(0).toUpperCase()}
                      </text>
                    </svg>
                  `),
              scaledSize: new window.google.maps.Size(markerSize, markerSize)
            }
          });

          // Create info window
          let infoContent = `
            <div style="padding: 8px; min-width: ${isMobile ? '150px' : '200px'}; max-width: ${isMobile ? '250px' : '300px'};">
              <h4 style="margin: 0 0 8px 0; color: #1976d2; font-size: ${isMobile ? '14px' : '16px'};">${member.username}</h4>
              <p style="margin: 0 0 4px 0; font-size: ${isMobile ? '11px' : '12px'}; color: #666;">
                <strong>Role:</strong> ${member.role === 'creator' ? 'Trip Creator' : 'Member'}
              </p>
              <p style="margin: 0 0 4px 0; font-size: ${isMobile ? '11px' : '12px'}; color: #666;">
                <strong>Updated:</strong> ${(() => {
                  const now = new Date();
                  const locationTime = new Date(member.location_updated_at);
                  const diffMinutes = Math.floor((now - locationTime) / (1000 * 60));
                  
                  if (diffMinutes < 1) return 'Just now';
                  if (diffMinutes < 60) return `${diffMinutes}m ago`;
                  
                  const diffHours = Math.floor(diffMinutes / 60);
                  if (diffHours < 24) return `${diffHours}h ago`;
                  
                  const diffDays = Math.floor(diffHours / 24);
                  return `${diffDays}d ago`;
                })()}
              </p>
              <p style="margin: 0; font-size: ${isMobile ? '10px' : '11px'}; color: #999;">
                ${Number(member.latitude).toFixed(6)}, ${Number(member.longitude).toFixed(6)}
              </p>`;

          // Add note if multiple members are at same location
          if (group.members.length > 1) {
            const otherMembers = group.members.filter(m => m.id !== member.id);
            if (otherMembers.length > 0) {
              infoContent += `
                <hr style="margin: 8px 0; border: none; border-top: 1px solid #eee;">
                <p style="margin: 0; font-size: ${isMobile ? '10px' : '11px'}; color: #666;">
                  <strong>Also here:</strong> ${otherMembers.map(m => m.username).join(', ')}
                </p>`;
            }
          }

          infoContent += `</div>`;

          const infoWindow = new window.google.maps.InfoWindow({
            content: infoContent
          });

          marker.addListener('click', () => {
            // Close all other info windows
            infoWindowsRef.current.forEach(iw => iw.close());
            infoWindow.open(mapInstanceRef.current, marker);
          });

          markersRef.current.set(memberId, marker);
          infoWindowsRef.current.set(memberId, infoWindow);
        }
      });
    });

    // Handle user location marker
    if (userLocation.latitude && userLocation.longitude) {
      const userPosition = {
        lat: Number(userLocation.latitude),
        lng: Number(userLocation.longitude)
      };

      // Check if user location is different from existing member locations
      const isUserLocationUnique = !memberLocations.some(member => 
        Math.abs(Number(member.latitude) - userPosition.lat) < 0.0001 &&
        Math.abs(Number(member.longitude) - userPosition.lng) < 0.0001
      );

      if (isUserLocationUnique) {
        const existingUserMarker = markersRef.current.get('user');
        if (existingUserMarker) {
          // Update existing user marker position if it changed significantly
          const currentPos = existingUserMarker.getPosition();
          if (currentPos) {
            const distance = Math.sqrt(
              Math.pow(currentPos.lat() - userPosition.lat, 2) + 
              Math.pow(currentPos.lng() - userPosition.lng, 2)
            );
            
            if (distance > 0.00001) { // Only update if moved more than ~1 meter
              existingUserMarker.setPosition(userPosition);
            }
          } else {
            // If no position, set it now
            existingUserMarker.setPosition(userPosition);
          }
        } else {
          // Create new user marker
          const markerSize = isMobile ? 28 : 32;
          
          const userMarker = new window.google.maps.Marker({
            position: userPosition,
            map: mapInstanceRef.current,
            title: 'Your Location',
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="12" fill="#f44336" stroke="white" stroke-width="3"/>
                  <circle cx="16" cy="16" r="4" fill="white"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(markerSize, markerSize)
            }
          });

          const userInfoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; min-width: ${isMobile ? '120px' : '150px'};">
                <h4 style="margin: 0 0 8px 0; color: #f44336; font-size: ${isMobile ? '14px' : '16px'};">Your Location</h4>
                <p style="margin: 0 0 4px 0; font-size: ${isMobile ? '11px' : '12px'}; color: #666;">
                  <strong>Accuracy:</strong> ±${Math.round(userLocation.accuracy)}m
                </p>
                <p style="margin: 0; font-size: ${isMobile ? '10px' : '11px'}; color: #999;">
                  ${userPosition.lat.toFixed(6)}, ${userPosition.lng.toFixed(6)}
                </p>
              </div>
            `
          });

          userMarker.addListener('click', () => {
            infoWindowsRef.current.forEach(iw => iw.close());
            userInfoWindow.open(mapInstanceRef.current, userMarker);
          });

          markersRef.current.set('user', userMarker);
          infoWindowsRef.current.set('user', userInfoWindow);
        }
      }
    }

    // Remove markers for members no longer in the list
    const currentMemberIds = new Set(memberLocations.map(m => m.id));
    markersRef.current.forEach((marker, memberId) => {
      if (memberId !== 'user' && !currentMemberIds.has(memberId)) {
        marker.setMap(null);
        markersRef.current.delete(memberId);
        const infoWindow = infoWindowsRef.current.get(memberId);
        if (infoWindow) {
          infoWindow.close();
          infoWindowsRef.current.delete(memberId);
        }
      }
    });

    // Fit bounds to show all markers (only if we have markers)
    if (markersRef.current.size > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markersRef.current.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      
      // Add some padding to the bounds
      mapInstanceRef.current.fitBounds(bounds);
      
      // Ensure minimum zoom level for mobile
      const listener = mapInstanceRef.current.addListener('bounds_changed', () => {
        if (mapInstanceRef.current.getZoom() > 15) {
          mapInstanceRef.current.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [memberLocations, userLocation, isMobile]);

  // Debounced marker update function
  const debouncedUpdateMarkers = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      updateMarkers();
      updateTimeoutRef.current = null;
    }, 100); // 100ms debounce for mobile performance
  }, [updateMarkers]);

  // Initialize map only once
  const initializeMap = useCallback(() => {
    if (isInitializedRef.current || !mapRef.current || !window.google || !window.google.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 0, lng: 0 },
      zoom: 2,
      mapTypeId: 'roadmap',
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    // Store map instance for later use
    mapInstanceRef.current = map;
    isInitializedRef.current = true;
    resizeUnbindRef.current?.();
    resizeUnbindRef.current = bindGoogleMapResize(map, mapRef.current);

    // Update markers after map is initialized
    updateMarkers();

    if (onLoadRef.current) onLoadRef.current(map);
  }, [updateMarkers]);

  // Update onLoad ref when prop changes
  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  // Initialize map only once when component mounts
  useEffect(() => {
    initializeMap();
  }, [initializeMap]);

  // Update markers when location data changes (debounced)
  useEffect(() => {
    if (mapInstanceRef.current && isInitializedRef.current) {
      debouncedUpdateMarkers();
    }
  }, [memberLocations, userLocation, debouncedUpdateMarkers]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    const markers = markersRef.current;
    const infoWindows = infoWindowsRef.current;
    return () => {
      resizeUnbindRef.current?.();
      resizeUnbindRef.current = null;
      // Clean up timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      // Clean up markers and info windows on unmount
      if (markers) {
        markers.forEach(marker => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        markers.clear();
      }
      if (infoWindows) {
        infoWindows.forEach(infoWindow => {
          if (infoWindow && infoWindow.close) {
            infoWindow.close();
          }
        });
        infoWindows.clear();
      }
    };
  }, []);

  return (
    <div
      ref={mapRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    />
  );
});

// Map render function for Google Maps Wrapper
const render = (status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: { xs: 300, sm: 460 } }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading Google Maps...</Typography>
        </Box>
      );
    case Status.FAILURE:
      return (
        <Alert severity="error">
          Failed to load Google Maps. Please check your internet connection and API key.
        </Alert>
      );
    default:
      return null;
  }
};

const TripMemberMap = ({ tripId, trip }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [memberLocations, setMemberLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [showListView, setShowListView] = useState(false);
  const mapRef = useRef(null);

  // Stable onLoad callback to prevent re-renders
  const handleMapLoad = useCallback((map) => { 
    if (mapRef.current !== map) {
      mapRef.current = map; 
    }
  }, []);
  
  const {
    location: userLocation,
    error: locationError,
    isWatching,
    startWatching,
    stopWatching
  } = useGeolocation();

  const { connected, addEventListener, subscribeToTrip, unsubscribeFromTrip } = useGlobalWebSocket();

  // Check if trip has ended
  const isTripEnded = useCallback(() => {
    if (!trip || !trip.end_date) return false;
    const now = new Date();
    const endDate = new Date(trip.end_date);
    return now > endDate;
  }, [trip]);

  // Fetch initial member locations
  const fetchMemberLocations = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get(`/trips/${tripId}/locations`);
      setMemberLocations(response.data);
    } catch (err) {
      setError('Failed to fetch member locations');
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  // Fetch user preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await api.get('/preferences');
      setPreferences(response.data);
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  }, []);

  // NOTE: Trip-ended check is combined with the preferences effect below
  // to avoid a ping-pong loop where one effect starts watching and another stops it.

  // Set up location update listener
  useEffect(() => {
    if (!addEventListener) return;
    
    const removeListener = addEventListener('location_update', (data) => {
      if (data.type === 'location_update') {
        setMemberLocations(prev => {
          // Check if this update is actually different from current data
          const existingMember = prev.find(member => member.id === data.user_id);
          if (existingMember) {
            const existingLat = Number(existingMember.latitude);
            const existingLng = Number(existingMember.longitude);
            const newLat = Number(data.latitude);
            const newLng = Number(data.longitude);
            
            // Only update if location changed significantly (more than ~10 meters)
            const distance = Math.sqrt(
              Math.pow(newLat - existingLat, 2) + 
              Math.pow(newLng - existingLng, 2)
            );
            
            if (distance < 0.0001) {
              return prev; // No significant change, don't update
            }
          }
          
          // Update the member's location
          const filtered = prev.filter(member => member.id !== data.user_id);
          return [...filtered, {
            id: data.user_id,
            username: data.username,
            latitude: data.latitude,
            longitude: data.longitude,
            location_updated_at: data.timestamp,
            last_seen: new Date().toISOString(),
            role: 'member'
          }];
        });
      }
    });

    // Set up periodic refresh of member locations (less frequent on mobile)
    const refreshInterval = setInterval(fetchMemberLocations, isMobile ? 60000 : 30000); // 60s on mobile, 30s on desktop

    return () => {
      removeListener();
      clearInterval(refreshInterval);
    };
  }, [addEventListener, fetchMemberLocations, isMobile]);

  // Add online status indicator
  const isUserOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    return (now - lastSeenDate) < 60000; // Consider user online if last seen within 1 minute
  };

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Format distance for display
  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance.toFixed(1)}km`;
  };

  // Format location age for display
  const formatLocationAge = (timestamp) => {
    const now = new Date();
    const locationTime = new Date(timestamp);
    const diffMinutes = Math.floor((now - locationTime) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  useEffect(() => {
    fetchMemberLocations();
    fetchPreferences();
  }, [fetchMemberLocations, fetchPreferences, tripId]);

  // Subscribe to trip for real-time updates
  useEffect(() => {
    if (subscribeToTrip) {
      subscribeToTrip(tripId);
    }
    return () => {
      if (unsubscribeFromTrip) {
        unsubscribeFromTrip(tripId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]); // Only depend on tripId to prevent infinite subscription loops

  // Single unified effect for managing location watching.
  // Combines trip-ended check and preferences check to avoid ping-pong loops
  // where one effect starts watching and another stops it endlessly.
  useEffect(() => {
    if (preferences === null) return;

    const shouldWatch = preferences.locationSharing && !isTripEnded();

    if (shouldWatch && !isWatching) {
      startWatching();
    } else if (!shouldWatch && isWatching) {
      stopWatching();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, isWatching]);

  // Memoize props for MapComponent to prevent unnecessary re-renders
  const memoizedMapProps = useMemo(() => ({
    memberLocations,
    userLocation,
    onLoad: handleMapLoad,
    isMobile
  }), [memberLocations, userLocation, handleMapLoad, isMobile]);

  // List view component
  const renderListView = () => {
    if (memberLocations.length === 0) {
      return (
        <Box sx={{ 
          p: { xs: 2, sm: 4 }, 
          textAlign: 'center', 
          color: 'text.secondary',
          minHeight: { xs: 300, sm: 400 }
        }}>
          <LocationOffIcon sx={{ fontSize: { xs: 40, sm: 48 }, mb: 2 }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
            No member locations available
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Members need to enable location sharing in their preferences to appear on the map.
            The map shows locations updated within the last 6 hours.
          </Typography>
          <Link 
            component={RouterLink} 
            to="/preferences" 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 0.5,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            <SettingsIcon fontSize="small" />
            Go to Preferences
          </Link>
        </Box>
      );
    }

    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ px: 1 }}>
          Trip Member Locations
        </Typography>
        <List sx={{ px: 0 }}>
          {memberLocations.map((member) => {
            const distance = userLocation.latitude && userLocation.longitude
              ? calculateDistance(
                  Number(userLocation.latitude),
                  Number(userLocation.longitude),
                  Number(member.latitude),
                  Number(member.longitude)
                )
              : null;

            return (
              <ListItem 
                key={member.id} 
                sx={{ 
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1, sm: 1.5 }
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: member.role === 'creator' ? 'primary.main' : 'success.main',
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {member.username.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant={isMobile ? "body2" : "body1"} component="span">
                        {member.username}
                      </Typography>
                      {member.role === 'creator' && (
                        <Chip 
                          label="Creator" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                        />
                      )}
                      <Box sx={{ 
                        width: { xs: 6, sm: 8 }, 
                        height: { xs: 6, sm: 8 }, 
                        borderRadius: '50%', 
                        backgroundColor: isUserOnline(member.last_seen || member.location_updated_at) ? '#4caf50' : '#9e9e9e',
                        ml: 'auto'
                      }} />
                    </Box>
                  }
                  secondary={
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ mt: 0.5 }}>
                      {distance !== null && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDistance(distance)} away
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {isUserOnline(member.last_seen || member.location_updated_at) 
                          ? 'Online' 
                          : `Last seen ${formatLocationAge(member.last_seen || member.location_updated_at)}`}
                      </Typography>
                    </Stack>
                  }
                />
                <LocationOnIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box>
        <DocumentSectionHead title={t('tripTabs.memberMap')} meta={t('tripTabs.sharingLocation', { count: 0 })} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  return (
    <Box>
      <DocumentSectionHead
        title={t('tripTabs.memberMap')}
        meta={t('tripTabs.sharingLocation', { count: memberLocations.length })}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2, mt: -1, flexWrap: 'wrap' }}>
        {googleMapsApiKey && (
          <Button
            onClick={() => setShowListView(!showListView)}
            sx={documentAddButtonSx}
          >
            {showListView ? t('tripTabs.map') : 'List'}
          </Button>
        )}
        <Button onClick={fetchMemberLocations} sx={documentAddButtonSx}>
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Location Error Alert */}
      {locationError && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                stopWatching();
                setTimeout(() => startWatching(), 1000);
              }}
            >
              Retry
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>Location Error:</strong> {locationError}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Make sure location services are enabled and you've granted permission to this site.
            {locationError.includes('timed out') && ' Try moving to an area with better GPS signal.'}
          </Typography>
        </Alert>
      )}

      {/* Map/List Container */}
      <Box sx={{
        ...documentMapBoxSx,
        height: { xs: '55vh', sm: 'min(70vh, 720px)' },
        minHeight: { xs: 300, sm: 460 },
        mb: 1,
      }}>
        {!googleMapsApiKey ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            Google Maps API key not configured. Showing list view only.
            <br />
            Add REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables.
          </Alert>
        ) : null}
        {googleMapsApiKey && !showListView ? (
          <Wrapper 
            apiKey={googleMapsApiKey}
            version="weekly"
            libraries={['places']}
            language="en"
            region="US"
            render={render}
            key={googleMapsApiKey + '-' + tripId}
          >
            <MapComponent 
              {...memoizedMapProps}
            />
          </Wrapper>
        ) : (
          renderListView()
        )}
      </Box>

      {/* Location Sharing Status */}
      {preferences && (
        <Alert 
          severity={
            isTripEnded() 
              ? "warning" 
              : preferences.locationSharing 
                ? "success" 
                : "info"
          } 
          sx={{ mt: 2 }}
          icon={
            isTripEnded() 
              ? <LocationOffIcon /> 
              : preferences.locationSharing 
                ? <LocationOnIcon /> 
                : <LocationOffIcon />
          }
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            justifyContent: 'space-between', 
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Box>
              <Typography variant="body2" sx={{ lineHeight: 1.43 }}>
                {isTripEnded() 
                  ? 'Location sharing is disabled because this trip has ended. Location tracking has been automatically stopped.'
                  : `Location sharing is ${preferences.locationSharing ? 'enabled' : 'disabled'}. ${
                      preferences.locationSharing 
                        ? 'Your location is being shared with trip members.' 
                        : 'Enable location sharing to appear on the map.'
                    }`
                }
              </Typography>
              {!isTripEnded() && preferences.locationSharing && !userLocation.latitude && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Waiting for location... Make sure you've allowed location access in your browser.
                </Typography>
              )}
            </Box>
            {!isTripEnded() && (
              <Link 
                component={RouterLink} 
                to="/preferences" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  ml: { xs: 0, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                <SettingsIcon fontSize="small" />
                Settings
              </Link>
            )}
          </Box>
        </Alert>
      )}

      {/* User Location Status */}
      {userLocation.latitude && userLocation.longitude && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Your location is being shared (accuracy: ±{Math.round(userLocation.accuracy)}m)
          </Typography>
        </Alert>
      )}

      {/* WebSocket Connection Status */}
      {connected && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Real-time location updates are active
          </Typography>
        </Alert>
      )}
      {!showListView && memberLocations.map((member, index) => (
        <Box
          key={member.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            py: 1.75,
            borderBottom: `1px solid ${DOCUMENT.rule}`,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: index === 1 ? DOCUMENT.accent : DOCUMENT.oxblood,
              color: index === 1 ? DOCUMENT.oxblood : DOCUMENT.ivory,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {getDocumentInitials(member.username)}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 500, fontSize: 15 }}>{member.username}</Typography>
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
              {isUserOnline(member.last_seen || member.location_updated_at)
                ? 'Online'
                : `Last seen ${formatLocationAge(member.last_seen || member.location_updated_at)}`}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TripMemberMap; 